import { useEffect } from 'react'
import { useTrackEvent } from '../../../utils/events/useTrackEvent.hook'
import { useNavV3Gate } from './useNavV3Gate'
import { getNavVersionDailyEventData } from './navV3.tracking'

// Per-browser key for the daily nav-version snapshot. Mirrors the
// useNavV3OptOutSurveyAccess pattern: localStorage is the right granularity
// because the v3 opt-in/out toggle is itself a per-browser localStorage flag,
// so the version we're reporting is browser-local too.
export const NAV_VERSION_DAILY_TRACKING_STORAGE_KEY = 'navVersionDailyTracking'

type NavVersionDailyStorage = {
  lastTrackedDate: string
}

// `YYYY-MM-DD` in the user's local timezone — matches what a user perceives
// as "today" rather than UTC's day boundary, so a midnight-PT page load is
// treated as a new day.
const getLocalDateKey = (): string => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const readStorage = (): NavVersionDailyStorage | null => {
  try {
    const raw = window.localStorage.getItem(NAV_VERSION_DAILY_TRACKING_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as NavVersionDailyStorage
    return typeof parsed?.lastTrackedDate === 'string' ? parsed : null
  } catch {
    return null
  }
}

const writeStorage = (value: NavVersionDailyStorage) => {
  try {
    window.localStorage.setItem(NAV_VERSION_DAILY_TRACKING_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // localStorage can throw in private browsing / quota-exceeded scenarios.
    // Failing silently is fine: worst case we re-fire next mount.
  }
}

// Fires `app.navVersion.daily` at most once per local calendar date per
// browser, capturing whichever nav version (`v2` or `v3`) the user is
// currently rendering. Per-switch tracking misses the silent majority who
// never click the version switcher — this surfaces baseline adoption.
//
// The snapshot is whatever version the user is on at the FIRST page load of
// the day; mid-day switches are covered by the version-switcher tracking
// (added separately) and don't re-fire this event.
export const useTrackNavVersionDaily = () => {
  const trackEvent = useTrackEvent()
  const { enabled } = useNavV3Gate()
  const version: 'v2' | 'v3' = enabled ? 'v3' : 'v2'

  useEffect(() => {
    const today = getLocalDateKey()
    if (readStorage()?.lastTrackedDate === today) return

    trackEvent(getNavVersionDailyEventData(version))
    writeStorage({ lastTrackedDate: today })
  }, [trackEvent, version])
}
